import { memo, useCallback } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

export const RESETTABLE_RANGE_PICKER_DATE_FORMAT = 'DD.MM.YYYY';

export type ResettableRangeValue = [Dayjs, Dayjs];
export type ResettableRangePickerValue = ResettableRangeValue | null;
export type ResettableRangeClearMode = 'today' | 'empty';

type NullableRangeValue = [Dayjs | null, Dayjs | null] | null;
type ChangeFormatString = [string, string] | [];

interface RangePreset {
  label: ReactNode;
  value: ResettableRangeValue;
}

interface Props {
  value: ResettableRangePickerValue;
  onChange: (
    values: ResettableRangePickerValue,
    formatString: ChangeFormatString,
  ) => void;
  className?: string;
  style?: CSSProperties;
  placeholder?: [string, string];
  presets?: RangePreset[];
  clearMode?: ResettableRangeClearMode;
  disabled?: boolean;
}

const toTodayRange = (): ResettableRangeValue => {
  const today = dayjs();
  return [today, today];
};

const formatRange = (range: ResettableRangeValue): [string, string] => [
  range[0].format(RESETTABLE_RANGE_PICKER_DATE_FORMAT),
  range[1].format(RESETTABLE_RANGE_PICKER_DATE_FORMAT),
];

const isCompleteRange = (
  value: NullableRangeValue,
): value is ResettableRangeValue => Boolean(value?.[0] && value?.[1]);

export const isTodayRange = (value: ResettableRangePickerValue) => {
  if (!value) {
    return false;
  }

  const today = dayjs();
  return value[0].isSame(today, 'day') && value[1].isSame(today, 'day');
};

const normalizeFormatString = (
  value: ResettableRangeValue,
  formatString: string[],
): [string, string] => [
  formatString[0] || value[0].format(RESETTABLE_RANGE_PICKER_DATE_FORMAT),
  formatString[1] || value[1].format(RESETTABLE_RANGE_PICKER_DATE_FORMAT),
];

export const ResettableRangePicker = memo(
  ({
    value,
    onChange,
    className,
    style,
    placeholder = ['Период звонков', 'Период звонков'],
    presets,
    clearMode = 'today',
    disabled = false,
  }: Props) => {
    const handleChange = useCallback(
      (nextValue: NullableRangeValue, formatString: string[]) => {
        if (!isCompleteRange(nextValue)) {
          if (clearMode === 'empty') {
            onChange(null, []);
            return;
          }

          const todayRange = toTodayRange();
          onChange(todayRange, formatRange(todayRange));
          return;
        }

        onChange(nextValue, normalizeFormatString(nextValue, formatString));
      },
      [clearMode, onChange],
    );

    return (
      <DatePicker.RangePicker
        allowClear={
          clearMode === 'empty' ? value !== null : !isTodayRange(value)
        }
        className={className}
        format={RESETTABLE_RANGE_PICKER_DATE_FORMAT}
        onChange={handleChange}
        placeholder={placeholder}
        presets={presets}
        style={style}
        value={value}
        disabled={disabled}
      />
    );
  },
);

ResettableRangePicker.displayName = 'ResettableRangePicker';
